/**
 * TASK-182: KYC Extraction Review Modal
 *
 * Displays AI-extracted KYC data using the full KYC Wizard component.
 * Reuses all field definitions, validations, and UI from the main wizard.
 *
 * When user approves: Saves to KYC database + clears staging
 */

import React, { useState, useEffect } from 'react'
import { AlertCircle, Loader } from 'lucide-react'
import { httpClient } from '../../../lib/httpClient'
import { toast } from 'sonner'
import KYCWizard from '../../kyc/KYCWizard'

interface KYCExtractionReviewProps {
  sessionId: string
  clientId: string
  onApproved: () => void
  onClose: () => void
}

export const KYCExtractionReview: React.FC<KYCExtractionReviewProps> = ({
  sessionId,
  clientId,
  onApproved,
  onClose
}) => {
  const [loading, setLoading] = useState(true)
  const [prefillData, setPrefillData] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadStagingData()
  }, [])

  /**
   * Unwrap confidence-wrapped values to plain values
   * {value: "Jorge", confidence: 0.92} -> "Jorge"
   */
  const unwrapConfidenceValues = (obj: any): any => {
    if (obj === null || obj === undefined) return obj

    // If it's a confidence wrapper {value, confidence}, extract the value
    if (typeof obj === 'object' && 'value' in obj && 'confidence' in obj) {
      return unwrapConfidenceValues(obj.value) // Recursively unwrap in case value is also wrapped
    }

    // If it's an array, unwrap each item
    if (Array.isArray(obj)) {
      return obj.map(item => unwrapConfidenceValues(item))
    }

    // If it's an object, unwrap each property
    if (typeof obj === 'object') {
      const unwrapped: any = {}
      for (const key in obj) {
        unwrapped[key] = unwrapConfidenceValues(obj[key])
      }
      return unwrapped
    }

    // Plain value, return as is
    return obj
  }

  /**
   * Load data from staging using staging_id parameter
   */
  const loadStagingData = async () => {
    try {
      setLoading(true)
      setError(null)

      console.log('🔍 [TASK-191] Loading KYC data from staging:', sessionId)

      // Fetch from staging endpoint with staging_id
      const url = `/api/clients/${clientId}/kyc-prefill?staging_id=${sessionId}`
      const response = await httpClient.get(url)

      if (response.pending_extraction) {
        console.log('✅ [TASK-191] Found staging data:', response.pending_extraction)

        const extraction = response.pending_extraction.data

        // BUGFIX: kyc_data comes as JSON STRING, parse it first
        const kycDataObj = typeof extraction.kyc_data === 'string'
          ? JSON.parse(extraction.kyc_data)
          : extraction.kyc_data

        console.log('📋 [TASK-191] Parsed KYC data (with confidence wrappers):', kycDataObj)

        // BUGFIX 2: Unwrap confidence-wrapped values
        // The wizard expects plain values, not {value, confidence} objects
        const unwrappedData = unwrapConfidenceValues(kycDataObj)
        console.log('📋 [TASK-191] Unwrapped data for wizard:', unwrappedData)

        // Filter out empty sections to hide steps without data
        const filteredData: any = {}
        for (const key in unwrappedData) {
          const value = unwrappedData[key]

          // Skip empty values
          if (value === null || value === undefined || value === '') continue

          // Skip empty arrays
          if (Array.isArray(value) && value.length === 0) continue

          // Skip empty objects
          if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) continue

          // Keep sections with data
          filteredData[key] = value
        }

        console.log('📋 [TASK-191] Filtered data (only sections with data):', filteredData)
        console.log('📊 [TASK-191] Sections with data:', Object.keys(filteredData))

        // Set prefill data for KYCWizard
        setPrefillData(filteredData)
      } else {
        setError('No pending extraction found for this session.')
      }
    } catch (err: any) {
      console.error('❌ [TASK-191] Failed to load staging data:', err)
      setError(err.message || 'Failed to load KYC extraction')
    } finally {
      setLoading(false)
    }
  }

  /**
   * Handle wizard completion (user clicks "Submit" in wizard)
   * This approves the extraction and saves to database
   */
  const handleWizardComplete = async (formData: any) => {
    try {
      console.log('✅ [TASK-191] User completed wizard - approving extraction and saving to database')

      // Call the approval endpoint
      // This will: 1) Save KYC profile to database, 2) Clear staging
      await httpClient.post(
        `/api/clients/${clientId}/kyc/approve-extraction`,
        {
          session_id: sessionId,
          approved_data: {
            kyc_data: formData
          }
        }
      )

      console.log('✅ [TASK-191] Extraction approved and saved to database')
      toast.success('KYC data approved and saved successfully!')
      onApproved()
    } catch (err: any) {
      console.error('❌ [TASK-191] Approval failed:', err)
      toast.error('Failed to save KYC data: ' + (err.message || 'Unknown error'))
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4">
          <div className="flex flex-col items-center gap-4">
            <Loader className="w-12 h-12 text-blue-600 animate-spin" />
            <div className="text-center">
              <div className="text-lg font-semibold text-slate-900 mb-2">
                Loading Extracted Data...
              </div>
              <div className="text-sm text-slate-600">
                Retrieving document extraction for review
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <h3 className="text-lg font-semibold text-slate-900">Failed to Load</h3>
          </div>
          <p className="text-sm text-slate-600 mb-6">{error}</p>
          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
            >
              Close
            </button>
            <button
              onClick={loadStagingData}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Show KYC Wizard with prefilled data
  return (
    <div className="fixed inset-0 z-50 bg-white">
      {/* Banner indicating this is review mode */}
      <div className="bg-blue-50 border-b border-blue-200 px-6 py-4">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900">
              📄 Document Review Mode
            </p>
            <p className="text-xs text-blue-700 mt-1">
              Review the AI-extracted information from the uploaded document. Navigate through each section using the Next/Previous buttons or sidebar. You can edit any field before submitting. Empty required fields are allowed.
            </p>
          </div>
        </div>
      </div>

      {/* KYC Wizard with prefilled data from staging */}
      <KYCWizard
        clientId={clientId}
        clientName="" // Not needed for review
        onClose={onClose}
        onComplete={handleWizardComplete}
        externalPrefillData={prefillData}
        reviewMode={true}
      />
    </div>
  )
}
